/**
 * Rate limiter implementation using a token bucket algorithm
 */

// Store for rate limiting buckets
const rateLimiters = new Map();

class TokenBucket {
  constructor(maxTokens, refillRate) {
    this.maxTokens = maxTokens;
    this.refillRate = refillRate; // tokens per millisecond
    this.tokens = maxTokens;
    this.lastRefill = Date.now();
  }

  async getToken() {
    this.refill();

    if (this.tokens < 1) {
      const waitTime = (1 - this.tokens) / this.refillRate;
      await new Promise((resolve) => setTimeout(resolve, waitTime));
      this.refill();
    }

    this.tokens -= 1;
    return true;
  }

  refill() {
    const now = Date.now();
    const timePassed = now - this.lastRefill;
    const refillAmount = timePassed * this.refillRate;

    this.tokens = Math.min(this.maxTokens, this.tokens + refillAmount);
    this.lastRefill = now;
  }
}

/**
 * Rate limiting function that can be used across the application
 * @param {string} key - Unique identifier for the rate limit bucket
 * @param {Object} options - Rate limiting options
 * @param {number} options.maxRequests - Maximum number of requests allowed in the time window
 * @param {boolean} options.perMinute - If true, rate limit is per minute, otherwise per second
 * @returns {Promise<void>}
 * @throws {Error} If rate limit is exceeded
 */
export const rateLimit = async (
  key,
  { maxRequests = 10, perMinute = false }
) => {
  if (!rateLimiters.has(key)) {
    // Calculate refill rate (tokens per millisecond)
    const timeWindow = perMinute ? 60 * 1000 : 1000; // Convert to milliseconds
    const refillRate = maxRequests / timeWindow;

    rateLimiters.set(key, new TokenBucket(maxRequests, refillRate));
  }

  const limiter = rateLimiters.get(key);

  try {
    await limiter.getToken();
  } catch (error) {
    throw new Error(`Rate limit exceeded for ${key}: ${error.message}`);
  }
};

export default {
  rateLimit,
};
